import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  DateCell,
  ListFilters,
  ListSearchForm,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import dataService from "../../../helpers/dataService";
import tools from "../../../helpers/tools";
import { sidePanelActions } from "../../../context/actions/sidePanelActions";
import { modalActions } from "../../../context/actions/modalActions";
import useStore from "../../../context/useStore";
import StatutPropositionBadge from "./components/StatutPropositionBadge";
import DetailPropositionSanctionFT, { tonLigne } from "./components/DetailPropositionSanctionFT";
import { estAnonymise } from "../../../helpers/allocataire";
import {
  BoutonRelancer,
  BoutonVoir,
  CelluleAllocataire,
  Note,
  libelleBilanRelance,
  nomAllocataire,
  ouvrirConfirmationRelanceEnMasse,
} from "./components/blocsSanctionFT";
import {
  PROPOSITIONS_DEFAULT_FILTERS,
  PROPOSITIONS_ENDPOINT,
  PROPOSITIONS_FILTER_DEFINITIONS,
  PROPOSITIONS_FILTER_GROUPS,
  PROPOSITIONS_PERSIST_KEY,
  RELANCER_PROPOSITIONS_EN_ECHEC_ENDPOINT,
  buildPropositionsPayload,
  propositionsAtoms,
  relancerPropositionEndpoint,
} from "./sanctionsFTListConfig";

export const FILTERS_PANEL_ID = "propositions-ft-filters";
const FILTERS_OPEN_KEY = "espoar.boa.sanctionsFT.propositions.filtersOpen";

const COUNT_LABELS = { none: "Aucune proposition", one: "proposition", many: "propositions" };

// Une ligne par événement France Travail (décision du 16/09/2026) : une levée
// greffée sur une sanction a sa propre ligne, datée de la demande, en plus de
// la mention sous la ligne de la sanction. Une proposition née d'une levée n'a
// que la ligne de la levée.
export const CelluleProposition = ({ proposition: el }) =>
  el.ligne === "LEVEE" ? (
    <>
      <span className="cell-primary">
        <i className="fa fa-unlock-alt mr-1 cell-note--info" aria-hidden="true" />
        Levée de suspension
      </span>
      <Note>
        {el.detailLevee?.sanctionOrigineLabel
          ? `Sanction visée : ${el.detailLevee.sanctionOrigineLabel}`
          : el.familleManquementLabel}
      </Note>
      {el.detailLevee?.reponse && (
        <Note
          ton={el.detailLevee.reponse.typeEvenement === "VALID_LEVEE_SUSP" ? "ok" : "ko"}
          icon="reply"
        >
          {el.detailLevee.reponse.label}, transmise à France Travail
        </Note>
      )}
    </>
  ) : (
    <>
      <span className="cell-primary">{el.propositionFT?.label ?? "—"}</span>
      <Note>{el.familleManquementLabel}</Note>
      {el.detailLevee && (
        <Note ton="info" icon="unlock">
          Levée demandée
          {el.detailLevee.dateDemandeLevee
            ? ` le ${tools.formatDate(el.detailLevee.dateDemandeLevee)}`
            : ""}
          {" — voir sa ligne"}
        </Note>
      )}
    </>
  );

// La pastille dit où en est le dossier ; les lignes dessous, ce que France
// Travail en a fait et ce qui bloque, sans avoir à ouvrir la fiche.
export const CelluleEtat = ({ proposition: el }) => (
  <>
    <StatutPropositionBadge statut={el.statut} statutLabel={el.statutLabel} />
    {el.ligne !== "LEVEE" && el.decisionRemontee && (
      <Note
        ton={el.decisionRemontee.priseEnCompte ? "ok" : "warn"}
        icon={el.decisionRemontee.priseEnCompte ? "check-circle" : "exclamation-circle"}
      >
        {el.decisionRemontee.label}
        {el.decisionRemontee.priseEnCompte ? ", reçue par France Travail" : ", non prise en compte"}
      </Note>
    )}
    {el.blocage && (
      <Note ton="ko" icon="exclamation-triangle">
        Dernière tentative en échec
      </Note>
    )}
  </>
);

// Cinq colonnes plutôt que neuf : les informations qui se lisent ensemble
// sont empilées dans une même cellule, la seconde ligne en gris et plus
// petite. Le tableau reste lisible sur un écran étroit sans masquer de données.
export const getPropositionsColumns = ({ ouvrirDetail, relancer = null, relances = [] }) => [
  {
    key: "dateReception",
    header: "Reçue le",
    className: "col-date",
    render: (el) => <DateCell value={el.dateReception} />,
  },
  {
    key: "allocataire",
    header: "Allocataire",
    className: "col-recipient",
    render: (el) => (
      <CelluleAllocataire
        allocataire={el.allocataire}
        secondaire={`N° FT ${el.numeroFranceTravail} · sanction ${el.idConsequenceSanction}`}
      />
    ),
  },
  {
    key: "proposition",
    header: "Proposition France Travail",
    render: (el) => <CelluleProposition proposition={el} />,
  },
  {
    key: "etat",
    header: "État",
    render: (el) => <CelluleEtat proposition={el} />,
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (el) => (
      <>
        <BoutonVoir
          className="propositions-ft__voir"
          onClick={() => ouvrirDetail(el)}
          label={`Voir la proposition ${el.idConsequenceSanction}${
            nomAllocataire(el.allocataire) ? ` de ${nomAllocataire(el.allocataire)}` : ""
          }`}
        />
        {relancer && el.relancable && !estAnonymise(el.allocataire) && (
          <BoutonRelancer
            className="propositions-ft__relancer"
            onClick={() => relancer(el.idConsequenceSanction)}
            enCours={relances.includes(el.idConsequenceSanction)}
            alerte={Boolean(el.blocage)}
            label={`Relancer la proposition ${el.idConsequenceSanction}`}
          />
        )}
      </>
    ),
  },
];

// Clé de ligne : une même proposition peut porter deux lignes (sanction et levée).
const cleLigne = (el, index) => `${el.id || el.idConsequenceSanction}-${el.ligne || "SANCTION"}-${index}`;

const PropositionsSanctionFTList = () => {
  const [state, dispatch] = useStore();
  const spActions = sidePanelActions(state, dispatch);
  const mActions = modalActions(state, dispatch);
  const [filtersOpen, setFiltersOpen] = usePersistedState(FILTERS_OPEN_KEY, true);
  const [relances, setRelances] = useState([]);
  const [relanceEnMasseEnCours, setRelanceEnMasseEnCours] = useState(false);

  const list = useListPage({
    searchAtom: propositionsAtoms.searchAtom,
    resultsAtom: propositionsAtoms.resultsAtom,
    defaultFilters: PROPOSITIONS_DEFAULT_FILTERS,
    endpoint: PROPOSITIONS_ENDPOINT,
    buildPayload: buildPropositionsPayload,
    persistKey: PROPOSITIONS_PERSIST_KEY,
    definitions: PROPOSITIONS_FILTER_DEFINITIONS,
    groups: PROPOSITIONS_FILTER_GROUPS,
  });

  // La correction se fait ailleurs — une session EP à créer, une donnée à
  // saisir. Ce bouton ne fait que redemander le traitement sans attendre le
  // passage du cron.
  function relancer(idConsequenceSanction) {
    setRelances((ids) => [...ids, idConsequenceSanction]);
    dataService.post(
      relancerPropositionEndpoint(idConsequenceSanction),
      {},
      () => {
        toast.success(
          "Proposition remise en file : son issue apparaîtra ici dans quelques instants."
        );
        list.runSearch(list.search);
      },
      (erreur) => toast.error(erreur.other || "La relance a échoué"),
      () => setRelances((ids) => ids.filter((id) => id !== idConsequenceSanction))
    );
  }

  // Tout ce qui est en échec repart d'un coup : après un passage du cron où
  // France Travail a refusé en rafale, relancer dossier par dossier ne tient pas.
  // Confirmation obligatoire, dans la modale du back-office : la relance porte
  // sur tous les dossiers en échec, pas sur la page affichée, et peut
  // déclencher des centaines d'appels.
  function relancerToutEnEchec() {
    ouvrirConfirmationRelanceEnMasse(mActions, {
      phrase: "Toutes les propositions en échec seront remises en file",
      onConfirmer: lancerRelanceEnMasse,
    });
  }

  function lancerRelanceEnMasse() {
    setRelanceEnMasseEnCours(true);
    dataService.post(
      RELANCER_PROPOSITIONS_EN_ECHEC_ENDPOINT,
      {},
      (bilan) => {
        toast.success(libelleBilanRelance(bilan, "proposition"));
        list.runSearch(list.search);
      },
      (erreur) => toast.error(erreur.other || "La relance en masse a échoué"),
      () => setRelanceEnMasseEnCours(false)
    );
  }

  // Le tableau ne garde que de quoi repérer et trier ; la fiche complète
  // s'ouvre dans le panneau latéral, comme le détail d'une EP.
  function ouvrirDetail(proposition) {
    spActions.updateSidePanel({
      isOpen: true,
      content: (
        <DetailPropositionSanctionFT
          proposition={proposition}
          enCoursDeRelance={relances.includes(proposition.idConsequenceSanction)}
          onRelancer={(id) => {
            spActions.updateSidePanel({ isOpen: false, content: null });
            relancer(id);
          }}
        />
      ),
    });
  }

  const columns = getPropositionsColumns({ ouvrirDetail, relancer, relances });
  const aDesEchecs = list.items.some((el) => el.blocage);

  return (
    <div className="list-page propositions-ft">
      <ListSearchForm onSubmit={list.submit} aria-label="Recherche de propositions France Travail">
        <SearchToolbar
          id="propositions-ft-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="N° France Travail ou identifiant de sanction"
          isLoading={list.isLoading}
          isDirty={list.isDirty}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          inlineFiltersLayout="ligne"
          filtersLabel="Plus de filtres"
          filtersPanelId={FILTERS_PANEL_ID}
          filtersOpen={filtersOpen}
          onToggleFilters={list.hasPanelFilters ? () => setFiltersOpen((open) => !open) : null}
          activeFiltersCount={list.panelActiveFiltersCount}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
        {list.hasPanelFilters && (
          <ListFilters
            id={FILTERS_PANEL_ID}
            open={filtersOpen}
            groups={list.groups}
            filters={list.filters}
            onChange={list.onFilterChange}
            onSet={list.setFilter}
            onReset={list.resetFilters}
            canReset={list.canReset}
          />
        )}
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {formatResultCount(list.count, COUNT_LABELS)}
          </p>
          {aDesEchecs && (
            <div className="results-bar__actions">
              <button
                type="button"
                className="btn btn-sm btn-warning propositions-ft__relancer-tout"
                onClick={relancerToutEnEchec}
                disabled={relanceEnMasseEnCours}
                aria-busy={relanceEnMasseEnCours ? true : undefined}
                title="Remet en file toutes les propositions en échec, pas seulement celles de cette page"
              >
                <i className={`fa mr-1 ${relanceEnMasseEnCours ? "fa-spinner fa-spin" : "fa-redo"}`} aria-hidden="true" />
                Relancer tout ce qui est en échec
              </button>
            </div>
          )}
        </div>
        <DataTable
          caption="Propositions de sanction reçues de France Travail"
          columns={columns}
          rows={list.items}
          rowKey={cleLigne}
          // Liseré vert sur une ligne dont France Travail a reçu la décision,
          // orange sur un blocage, rouge sur un rejet : l'état se lit avant
          // même de lire la ligne.
          rowClassName={(el) => (tonLigne(el) ? `row--${tonLigne(el)}` : "")}
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          emptyState={{
            title: "Aucune proposition ne correspond à ces critères",
            hint: "Modifiez votre recherche ou vos filtres.",
            action: list.canReset
              ? { label: "Réinitialiser les filtres", onClick: list.resetFilters }
              : null,
          }}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="propositions"
        />
      </section>
    </div>
  );
};

export default PropositionsSanctionFTList;
